import type {
  Commonv1Location,
  V1AwaitingGuidanceRunState,
  V1NodeExecutedInputs,
  V1NodeExecutedOutputs,
} from '@/generated/hooks/bishop/models';
import type { IBrowserInstanceValue } from '@/modules/outputs/utils';
import type { ICharacterRange } from '@/shared/utils/source-map';

/**
 * Enumeration of execution log event types.
 */
export enum EExecutionLogEventType {
  BOOK_FUNCTION_CALL = 'book_function_call',
  AWAITING_GUIDANCE = 'awaiting_guidance',
  STATEMENT_NODE = 'statement_node',
}

/**
 * Type representing a book function call extracted from a run event.
 *
 * Based on the structure found in execution_journal.node_executed.book_function_call.
 */
export type TBookFunctionCall = {
  book_function?: {
    connection_spy_identifier?: string;
    book_name?: string;
    function_name?: string;
    code_location?: Commonv1Location;
  };
  code_location?: Commonv1Location;
  inputs?: V1NodeExecutedInputs;
  output?: V1NodeExecutedOutputs;
};

/**
 * Type representing a statement node extracted from a run event.
 *
 * Based on the structure found in execution_journal.node_executed with node_kind="Statement".
 */
export type TStatementNode = {
  /** Code location with start_byte and end_byte */
  code_location?: Commonv1Location;
  /** Input values for the statement */
  inputs?: V1NodeExecutedInputs;
  /** Output values from the statement */
  outputs?: V1NodeExecutedOutputs;
  /** Unique node identifier */
  node_id?: string;
};

/**
 * Union type representing different types of execution log events.
 */
export type TExecutionLogEvent =
  | {
      type: EExecutionLogEventType.BOOK_FUNCTION_CALL;
      data: TBookFunctionCall;
      /** ISO timestamp when the event occurred */
      timestamp?: string;
    }
  | {
      type: EExecutionLogEventType.AWAITING_GUIDANCE;
      data: V1AwaitingGuidanceRunState;
      /** ISO timestamp when the event occurred */
      timestamp?: string;
    }
  | {
      type: EExecutionLogEventType.STATEMENT_NODE;
      data: TStatementNode;
      /** ISO timestamp when the event occurred */
      timestamp?: string;
    };

/**
 * Extended V1NodeExecuted type that includes book_function_call and node_kind.
 *
 * The generated types may not include book_function_call or node_kind, but they exist
 * at runtime in the execution journal.
 */
export type V1NodeExecutedExtended = {
  book_function_call?: TBookFunctionCall;
  code_location?: Commonv1Location;
  node_kind?: string;
  inputs?: V1NodeExecutedInputs;
  outputs?: V1NodeExecutedOutputs;
  node_id?: string;
  [key: string]: unknown;
};

/**
 * Represents a focused run log item for highlighting.
 *
 * Used to track which run log item is currently hovered or opened,
 * enabling highlight orchestration in the editor.
 *
 * Supports two modes:
 * 1. Spy location mode: Uses `location` (spy byte positions) which are mapped to English via codeMapping
 * 2. English location mode: Uses `englishLocation` (English byte positions) which are converted directly to chars
 */
export interface IFocusedRunLogItem {
  /** The code location to highlight (spy code location). Used for event highlights. */
  location?: Commonv1Location;
  /** Direct English byte positions for highlighting. Used for section highlights. */
  englishLocation?: { startByte: number; endByte: number };
  /** Whether this is an exception type (determines highlight color) */
  isException: boolean;
}

/**
 * A request, from outside the run log, to open one row, scroll it into view
 * and flash it. The nonce changes on every request so asking for the same
 * row twice still reacts.
 */
export interface IRunLogFocusRequest {
  /** Section key (classic log) or journal node id (manifest log). */
  id: string;
  nonce: number;
}

/**
 * Represents a parsed variable from the @vars comment in english code.
 */
export interface IEnglishCodeVar {
  /** Display label for the variable */
  label: string;
  /** Variable name/identifier */
  var: string;
}

/**
 * Represents a parsed section of english code.
 *
 * Each section corresponds to a statement in the english code,
 * delimited by @vars comments.
 */
export interface IEnglishCodeSection {
  /** Unique key for the section (format: "startByte-endByte") */
  key: string;
  /** Starting byte position in the english code */
  startByte: number;
  /** Ending byte position in the english code */
  endByte: number;
  /** Start byte position for display text, excluding leading whitespace (0-indexed) */
  displayStartByte: number;
  /** End byte position for display text, excluding the @vars comment (0-indexed) */
  displayEndByte: number;
  /** Full content including the @vars comment */
  content: string;
  /** Display text (content without the @vars comment) */
  displayText: string;
  /** Variables defined in the @vars comment */
  vars: IEnglishCodeVar[];
  /** Output variable names, or null if no outputs */
  outputs: string[] | null;
  /** Section index (0-based) */
  index: number;
}

/**
 * Status of a run log section derived from its events.
 */
export enum ESectionStatus {
  /** Section has no events yet (not reached or no logs) */
  DEFAULT = 'default',
  /** Section has events and execution completed */
  COMPLETED = 'completed',
  /** Section is paused awaiting user guidance */
  PAUSED = 'paused',
  /** Section is executing right now */
  RUNNING = 'running',
}

/**
 * Represents an english code section with grouped execution events.
 *
 * Extends IEnglishCodeSection to include the events that occurred
 * during execution of this section.
 */
export interface IEnglishCodeSectionWithEvents extends IEnglishCodeSection {
  /** Execution events that belong to this section */
  events: TExecutionLogEvent[];
  /** ISO timestamp of the earliest event in this section */
  firstTimestamp?: string;
  /** ISO timestamp of the latest event in this section */
  lastTimestamp?: string;
}

/** A distinct browser session and the step that first invoked it. */
export interface IBrowserOperatorStep {
  /** Distinct session key (session_id or browser_name). */
  sessionKey: string;
  /** The browser instance handle for this session. */
  value: IBrowserInstanceValue;
  /** Invoking step's character ranges, in aop-code coordinates. */
  aopRanges: ICharacterRange[];
}

/**
 * Measured position of the Browser Operator connector + pill in
 * overlay-local coordinates.
 */
export interface IAnchorGeometry {
  /** X of the step's first word (connector start). */
  leftX: number;
  /** X of the box's right edge (connector end / pill anchor). */
  rightX: number;
  /** Y of the bottom of the step's last line. */
  lineBottomY: number;
  /** Whether the step is within the scroll viewport (else the pill is hidden). */
  visible: boolean;
}
